import { Injectable, EventEmitter } from '@angular/core';
import { SyncServerServiceProvider } from '../sync-server-service/sync-server-service';
import { Project } from '../../models/project';
//FIXME: should load (and cache) latest from API service
const Grafee = require('@paviasystems/grafee').Grafee;

// Bundle names are versioned with the app rather than shared with it.
//
// The bundle and the build move together - the sampling plan connections this app reads do not
// exist in the older bundles, and older builds would be given data they do not expect. Naming them
// separately lets both bundles be hosted at once, so a release is not a coordinated swap and a
// rollback does not need the bundle rolled back with it.
//
// Bump BOTH when the shape changes. The app picks between them by whether a project has been
// chosen, so leaving one behind sends half the sessions to the previous bundle - and the app fails
// open to old behaviour rather than erroring, which makes that mistake invisible.
const BUNDLE_PROJECT = 'MaterialsAppV2';
const BUNDLE_FULL = 'MaterialsAppFullV2';

@Injectable()
export class DistillingService
{
    project: Project = new Project(0,0,'','');
    graph: any;
    // The bundle's own Settings block, kept beside the graph because Grafee exposes no accessor
    // for it. SpecScopeProvider reads FilterOverrideModes from here.
    bundleSettings: any = null;
    onBundleChange: EventEmitter<any> = new EventEmitter();
    defaultChosen: boolean = false;
    // Survives bundle swaps so a capture can span a project change.

    constructor(private syncService: SyncServerServiceProvider)
    {
        this.selectDefaultProject().then((project) =>
            {
                if (!project)
                {
                    console.warn('No projects found for current user');
                    return;
                }
                this.setProject(project);
            })
    }

    setdefault(val: boolean = true){
        this.defaultChosen = val;
    }

    /**
     * The tests the project's sampling plan requires for a material on a set of line items.
     *
     * Queried against the plan node directly rather than by asking for IDTest narrowed by both
     * axes: IDLineItem and IDMaterial are each one hop from the node, so the paths are unambiguous,
     * whereas IDMaterial also reaches IDTest through the spec-year join tables and which route the
     * graph picks is not pinned down.
     *
     * @param {Array<number>} pLineItems The selected line items.
     * @param {number|Array<number>} pIDMaterial The selected material.
     * @return {Set<number>} The plan's tests, or null when the plan cannot answer — no graph, either
     * axis unset, or a project carrying no plan at all. Callers must treat null as "do not narrow".
     */
    getPlanTestIDs(pLineItems, pIDMaterial): Set<number>
    {
        if (!this.graph || !pIDMaterial || !pLineItems || !pLineItems.length)
        {
            return null;
        }
        let planRows;
        try
        {
            planRows = this.graph.listValues('IDProjectLineItemMaterialTest', { IDLineItem: pLineItems, IDMaterial: pIDMaterial });
        }
        catch (error)
        {
            console.error('Sampling plan lookup failed', error);
            return null;
        }
        if (!Array.isArray(planRows) || !planRows.length)
        {
            return null;
        }
        const decorated = this.graph.extractJoinedValues('IDProjectLineItemMaterialTest', planRows, ['IDTest']) || [];
        const planTests = new Set<number>();
        let rowsCarryingTest = 0;
        for (const row of decorated)
        {
            if (row && row.IDTest != null)
            {
                ++rowsCarryingTest;
                planTests.add(Number(row.IDTest));
            }
        }
        // A plan row names exactly one test, so anything short of one test per row means the plan
        // is only partly filled in. Narrowing on a partial set would hide tests the plan does
        // require while looking authoritative, which is worse than not narrowing at all — so treat
        // incomplete coverage the same as no plan and leave the list alone.
        if (!planTests.size || rowsCarryingTest < planRows.length)
        {
            return null;
        }
        return planTests;
    }

    setProject(project)
    {
        let projectPromise: any;
        if (!project || !project.IDProject)
        {
            this.graph = null;
            this.bundleSettings = null;

            projectPromise = this.selectDefaultProject();
        }
        else
        {
            if (this.project && project.IDProject == this.project.IDProject)
            {
                return Promise.resolve();
            }
            // Clear the previous project's graph synchronously so downstream
            // consumers never read a stale graph between `project` being
            // updated and the new bundle arriving.
            this.graph = null;
            this.bundleSettings = null;
            projectPromise = Promise.resolve(project);
        }
        return projectPromise.then((p) =>
            {
                const pro = p || project;
                if (!pro)
                {
                    return;
                }
                this.project = pro;
                return this.loadBundleForProject(pro).then((bundle) =>
                    {
                        this.graph = new Grafee(bundle);
                        const payload: any = bundle;
                        this.bundleSettings = (payload && (payload.Bundle ? payload.Bundle._Settings : payload._Settings)) || null;
                    })
                        .catch((error) =>
                            {
                                this.graph = null;
                                this.bundleSettings = null;
                                console.error('Failure loading materials bundle for project', { project: pro, error });
                            }).then(() => {
                                if (this.graph) {
                                    this.onBundleChange.emit({ graph: this.graph });
                                }
                            }, (err) => {
                                console.error(err);
                                console.log(new Error().stack);
                            });
            });
    }

    async loadBundleForProject(project)
    {
        let bundleString = this.defaultChosen ? BUNDLE_PROJECT : BUNDLE_FULL;
        return this.syncService.gopher(`/For/${bundleString}/Project/${project.IDProject}?LibraryVersion=${Grafee.version}`, 'Distilling');
    }

    async selectDefaultProject()
    {
        return this.syncService.gopher(`/0/1`, 'Projects').then((projects) =>
            {
                return projects[0];
            }).catch((error) =>
                {
                    console.error('Error loading default project.');
                    return null;
                });
    }
}



// WEBPACK FOOTER //
// ./src/providers/distilling-service/distilling-service.ts